import { Injectable } from '@angular/core';
import { firstValueFrom, Observable, Subject, Subscription, take } from 'rxjs';
import * as _ from 'lodash-es';

import { GridFactory } from './grid.service';
import { environment } from '../../../../environments/environment';

/**
 * Result emitted on the per-screen result stream after a fetch completes.
 *
 * `data` is the fully validated + decompressed + split-merged response payload
 * (ready for `GridDirective.processAndLoadData`); `error` is set when either
 * the HTTP call failed or the backend returned `callSuccess !== '1'`.
 */
export interface TabGridResult {
  screenKey: string;
  data?: any;
  error?: any;
  forceRefresh?: boolean;
}

export interface TabGridFetchOpts {
  /**
   * If true, an in-flight request for the SAME `screen_key` is cancelled
   * before the new one starts (typical filter-debounce within one tab).
   * Requests for OTHER screen_keys are never touched.
   */
  forceRefresh?: boolean;
  /**
   * Set to true for `grid-import*` screens — enables the legacy edge case
   * where a single placeholder row with `object_id: '1'` is treated as
   * "no data".
   */
  isImportScreen?: boolean;
  /**
   * Optional split-call configuration. When present, the loader fans out to
   * `GridFactory.loadGridDataSplit` instead of `loadGridData`.
   */
  split?: { filterArrays: any[] };
}

/**
 * Singleton owner of grid-fetch HTTP subscriptions, keyed by `screen_key`.
 *
 * Why this exists:
 *
 * `GridDirective` lives inside `<router-outlet>`, so switching workspace
 * tabs destroys the directive instance. If the directive owns the
 * subscription, that destroy cancels the in-flight HTTP request — which
 * means the legacy "tabs keep loading in the background while you navigate
 * around" UX is silently broken.
 *
 * Moving the subscription to a singleton service decouples the request
 * lifecycle from the directive lifecycle. Switching tabs only tears down
 * the UI subscription on the directive; the HTTP call keeps going, lands
 * its result on `screen.data`, flips `screen.cachedData = true`, and clears
 * `screen.loading_data` — all of which the workspace tab strip is binding
 * to via `[ngClass]`. When the user navigates back, `getCached()` short-
 * circuits the new fetch and the grid renders instantly.
 */
@Injectable({ providedIn: 'root' })
export class TabGridLoaderService {
  private inFlight = new Map<string, Subscription>();
  private results$ = new Map<string, Subject<TabGridResult>>();
  /**
   * Source of truth for cached payloads, keyed by `screen_key`.
   *
   * Stored independently from the screen object so that re-mounting a
   * workspace area (which gives us BRAND NEW screen object references for
   * the same `screen_key`) does NOT invalidate the cache. The new screens
   * have `cachedData=undefined`, but `getCached(key)` still resolves the
   * payload from this map and `WorkspaceComponent.hydrateTabDotStates`
   * lights up the orange dot on the new screen object.
   *
   * Equally important: `clearCache(newScreen)` evicts the map entry by key,
   * so a stale screen reference left over from a prior visit cannot keep
   * the "Filter" click serving stale data.
   */
  private cache = new Map<string, any>();
  /**
   * Tracks the screen object currently associated with an in-flight fetch
   * so `cancelInFlight` can clear `loading_data` even when callers only
   * have the `screen_key`. Replaced on every `fetchForScreen`.
   */
  private inFlightScreens = new Map<string, any>();
  /**
   * Tracks the request params that the in-flight fetch was issued with
   * (snapshot at fetch time). Used by `fetchForScreen` to detect when a
   * NEW invocation for the same `screen_key` carries different filter
   * params (e.g. user changed ME selection between two `setTabActive`
   * emissions while the first request is still on the wire). Without
   * this guard, the second invocation bails on the in-flight check and
   * the old request — issued with stale params — is the one whose
   * response renders, producing the classic "I selected one ME but the
   * grid shows data for all MEs" symptom on drill-down/cascade flows
   * that re-emit `setTabActive` without first calling `clearCache`.
   */
  private inFlightParams = new Map<string, any>();
  /**
   * Whitelist of request keys that materially change the response. Other
   * keys (display_*, debug helpers, jcd_ta_key…) are noise — comparing
   * them would force a needless cancel+restart on every CD pass.
   */
  private static readonly GRID_PARAM_KEYS_FOR_DIFF: readonly string[] = [
    'parent_me_string',
    'tax_year',
    'scenario',
    'jcd_key',
    'filter_by',
    'filing_key',
    'chart_filter',
    'system',
    'me_string',
    'cc_key',
    'combination_key',
  ];
  /**
   * Global stream of fetch errors across ALL tabs. Per-screen `results$`
   * only has a subscriber while the corresponding tab is active, so an
   * error landing on a background tab would otherwise be silently lost.
   * Subscribe here once at app shell level (e.g. `WorkspaceComponent`) to
   * surface a snackbar that includes the tab label for context.
   */
  private errors$$ = new Subject<{ screen: any; error: any }>();

  constructor(private gridFactory: GridFactory) {}

  /** Global error stream — see `errors$$` doc-comment. */
  get errors$(): Observable<{ screen: any; error: any }> {
    return this.errors$$.asObservable();
  }

  /**
   * Start (or restart, if `forceRefresh`) a fetch for the given workspace
   * screen. Mutates `screen.loading_data` immediately so the tab dot starts
   * blinking, and mutates `screen.data` / `screen.cachedData` /
   * `screen.loading_data` on success/error.
   */
  fetchForScreen(
    screen: any,
    url: string,
    params: any,
    opts: TabGridFetchOpts = {}
  ): void {
    if (!screen?.screen_key || !url) return;
    const key = String(screen.screen_key);

    const prev = this.inFlight.get(key);
    if (prev) {
      // Detect whether this re-entry is just a "user navigated around and
      // came back" (params identical to the in-flight request) or a real
      // params change (filter cascade landed with new ME / year / etc.
      // while the previous request is still on the wire). The second case
      // MUST cancel-and-restart even without explicit `forceRefresh`:
      // otherwise the in-flight response — built with the OLD params —
      // wins and the user sees the old result, which is exactly the
      // "selected single ME but grid shows all MEs" regression on
      // drill-down / cascade flows that re-emit `setTabActive` without
      // first going through `WorkspaceService.clearCache`.
      const prevParams = this.inFlightParams.get(key);
      const paramsChanged = !TabGridLoaderService.areGridParamsEqual(prevParams, params);
      if (!opts.forceRefresh && !paramsChanged) {
        this.inFlightScreens.set(key, screen);
        screen.loading_data = true;
        return;
      }
      prev.unsubscribe();
      this.inFlight.delete(key);
    }

    screen.loading_data = true;
    this.inFlightScreens.set(key, screen);
    this.inFlightParams.set(key, params);

    const obs = opts.split
      ? this.gridFactory.loadGridDataSplit(url, opts.split.filterArrays)
      : this.gridFactory.loadGridData(url, params);

    const sub = obs.pipe(take(1)).subscribe(
      (data: any) => this.handleResponse(key, this.inFlightScreens.get(key) || screen, data, opts),
      (error: any) => this.handleError(key, this.inFlightScreens.get(key) || screen, error, opts)
    );
    this.inFlight.set(key, sub);
  }

  /**
   * Compare two request-param snapshots restricted to the whitelist of
   * keys that actually change the backend response. Returns true when
   * both are equivalent (or both null/undefined).
   */
  private static areGridParamsEqual(a: any, b: any): boolean {
    if (a === b) return true;
    if (!a || !b) return false;
    for (const k of TabGridLoaderService.GRID_PARAM_KEYS_FOR_DIFF) {
      const av = a[k];
      const bv = b[k];
      if (av === bv) continue;
      // Normalize null/undefined/empty-string to a single "absent" form so
      // moves between those representations don't trigger a restart.
      const aMissing = av == null || av === '';
      const bMissing = bv == null || bv === '';
      if (aMissing && bMissing) continue;
      if (String(av) !== String(bv)) return false;
    }
    return true;
  }

  /**
   * Returns processed cached data for a screen if one was successfully
   * loaded earlier (and not invalidated since). Resolves from the `cache`
   * map keyed by `screen_key` — independent of any specific screen object
   * reference, so it survives workspace re-mounts.
   */
  getCached(screenKey: string | number): any {
    return this.cache.get(String(screenKey)) ?? null;
  }

  isLoading(screenKey: string | number): boolean {
    return this.inFlight.has(String(screenKey));
  }

  /**
   * Stream of per-screen fetch results. `GridDirective` subscribes here on
   * mount and processes each emit through `processAndLoadData`. The stream
   * outlives the directive — late subscribers will receive the next emit
   * (cache hit on revisit is handled separately via `getCached`).
   */
  getResultStream(screenKey: string | number): Observable<TabGridResult> {
    return this.getOrCreateStream(String(screenKey)).asObservable();
  }

  /**
   * Allow consumers (e.g. dashboards) to seed the cache directly with
   * already-processed data, skipping the fetch path.
   */
  setCache(screen: any, data: any): void {
    if (!screen?.screen_key) return;
    this.cache.set(String(screen.screen_key), data);
    screen.data = data;
    screen.cachedData = true;
  }

  /**
   * Drops both the cached payload and the cached-badge for a screen.
   * Does NOT touch any in-flight subscription — call `cancelInFlight`
   * separately if needed.
   *
   * Importantly, this evicts by `screen_key` so even a stale screen
   * reference from a prior workspace mount cannot keep stale data alive
   * for the next "Filter" click.
   */
  clearCache(screen: any): void {
    if (!screen) return;
    screen.cachedData = false;
    screen.data = null;
    if (screen.screen_key != null) {
      this.cache.delete(String(screen.screen_key));
    }
  }

  cancelInFlight(screenKey: string | number): void {
    const key = String(screenKey);
    const sub = this.inFlight.get(key);
    if (!sub) return;
    sub.unsubscribe();
    this.inFlight.delete(key);
    const screen = this.inFlightScreens.get(key);
    if (screen) screen.loading_data = false;
    this.inFlightScreens.delete(key);
    this.inFlightParams.delete(key);
  }

  // ---------------------------------------------------------------------------
  // Internals
  // ---------------------------------------------------------------------------

  private handleResponse(
    key: string,
    screen: any,
    data: any,
    opts: TabGridFetchOpts
  ): void {
    this.inFlight.delete(key);

    const cs = data?.callSuccess;
    if (cs !== '1' && cs !== 1 && String(cs) !== '1') {
      screen.loading_data = false;
      this.inFlightScreens.delete(key);
      this.inFlightParams.delete(key);
      const err = {
        type: 'callSuccess',
        message: data?.errorMessage || 'Grid call failed',
      };
      this.getOrCreateStream(key).next({
        screenKey: key,
        error: err,
        forceRefresh: opts.forceRefresh,
      });
      this.errors$$.next({ screen, error: err });
      return;
    }

    if (data.compressed && data.header_keys && data.data && !data.files) {
      data.data = this.decompressGridData(data.header_keys, data.data);
    }
    if (!data.data) data.data = [];

    if (
      opts.isImportScreen &&
      data.data.length === 1 &&
      data.data[0]?.object_id === '1' &&
      Object.keys(data.data[0]).length <= 1
    ) {
      data.data = [];
    }

    if (data.files && data.files.length > 0) {
      this.loadSplitFiles(key, screen, data, opts);
      return;
    }

    this.finalize(key, screen, data, opts);
  }

  private loadSplitFiles(
    key: string,
    screen: any,
    data: any,
    opts: TabGridFetchOpts
  ): void {
    const files: string[] = data.files;
    const requests = files.map((f: string) =>
      firstValueFrom(
        this.gridFactory.loadFile(
          environment.base_url +
            '/getGridDataFromFile?name=' +
            f +
            '&compressed=' +
            data.compressed
        )
      )
    );

    Promise.all(requests)
      .then((results: any[]) => {
        if (!data.data) data.data = [];
        results.forEach((r: any) => {
          if (r?.data) data.data = data.data.concat(r.data);
        });
        if (data.compressed && data.header_keys) {
          data.data = this.decompressGridData(data.header_keys, data.data);
        }
        delete data.files;
        this.finalize(key, screen, data, opts);
      })
      .catch((err: any) => this.handleError(key, screen, err, opts));
  }

  private finalize(
    key: string,
    screen: any,
    data: any,
    opts: TabGridFetchOpts
  ): void {
    this.cache.set(key, data);
    this.inFlightScreens.delete(key);
    this.inFlightParams.delete(key);
    screen.data = data;
    screen.cachedData = true;
    screen.loading_data = false;
    this.getOrCreateStream(key).next({
      screenKey: key,
      data,
      forceRefresh: opts.forceRefresh,
    });
  }

  private handleError(
    key: string,
    screen: any,
    error: any,
    opts: TabGridFetchOpts
  ): void {
    this.inFlight.delete(key);
    this.inFlightScreens.delete(key);
    this.inFlightParams.delete(key);
    screen.loading_data = false;
    this.getOrCreateStream(key).next({
      screenKey: key,
      error,
      forceRefresh: opts.forceRefresh,
    });
    this.errors$$.next({ screen, error });
  }

  private decompressGridData(headerKeys: string[], data: any[]): any[] {
    const result: any[] = [];
    _.each(data, (row: any) => {
      const obj: any = {};
      for (let i = 0; i < row.length; i++) {
        obj[headerKeys[i]] = row[i];
      }
      result.push(obj);
    });
    return result;
  }

  private getOrCreateStream(key: string): Subject<TabGridResult> {
    let s = this.results$.get(key);
    if (!s) {
      s = new Subject<TabGridResult>();
      this.results$.set(key, s);
    }
    return s;
  }
}
